/**
 * useSuspenseSets Hook - React Suspense対応のセット一覧取得
 */

import { useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '../../../lib/queryKeys'
import { setQueryOptions } from '../../../lib/queryClient'
import { useStore } from '../../../store'
import type { SetWithStats, SortOption, SortOrder, FilterOption } from './useSets'

/**
 * React Suspense対応のセット一覧を取得するフック
 * データ取得中は自動的にSuspenseがトリガーされる
 */
export function useSuspenseSets(options?: {
  sortBy?: SortOption
  sortOrder?: SortOrder
  filterBy?: FilterOption
}) {
  const {
    sortBy = 'createdAt',
    sortOrder = 'desc',
    filterBy = 'all'
  } = options || {}

  // 現在のストアの実装を使用
  const getAllSets = useStore(state => state.getAllSets)
  const appState = useStore(state => state.appState)

  // useSuspenseQueryを使用 - データ取得中は自動的にSuspenseがトリガーされる
  const { data } = useSuspenseQuery({
    queryKey: queryKeys.sets.list({ sortBy, sortOrder, filterBy }),
    queryFn: (): Promise<SetWithStats[]> => {
      const sets = getAllSets()
      
      // 統計情報を追加
      const setsWithStats: SetWithStats[] = sets.map(set => {
        const items = Object.values(appState.setItems).filter(
          item => item.setId === set.id
        )
        
        return {
          ...set,
          itemCount: items.length,
          lastModified: items.length > 0 
            ? new Date(Math.max(...items.map(i => new Date(i.metadata?.capturedAt || 0).getTime())))
            : set.createdAt,
          thumbnailUrl: items[0] ? `/api/images/${items[0].imageId}/thumbnail` : undefined
        }
      })

      return Promise.resolve(setsWithStats)
    },
    ...setQueryOptions,
  })

  // クライアントサイドでのソートとフィルタリング
  const processedData = useMemo(() => {
    if (!data) return []

    let filtered = [...data]

    // フィルタリング
    if (filterBy !== 'all') {
      filtered = filtered.filter(set => set.type === filterBy)
    }

    // ソート - 最新のセットが上に来るようにする
    filtered.sort((a, b) => {
      let compareValue = 0
      
      switch (sortBy) {
        case 'name':
          compareValue = (a.name || '').localeCompare(b.name || '')
          break
        case 'itemCount':
          compareValue = a.itemCount - b.itemCount
          break
        case 'createdAt':
        default:
          compareValue = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortOrder === 'asc' ? compareValue : -compareValue
    })

    return filtered
  }, [data, sortBy, sortOrder, filterBy])

  // Suspenseモードでは、dataは常に存在するのでloadingやerrorの状態は不要
  return {
    sets: processedData,
    totalCount: data?.length || 0,
    filteredCount: processedData.length,
  }
}